import React, { useCallback, useEffect, useRef, useState } from "react";
import { useStateValue } from "../../StateProvider";
import {
  collectUserTalentAncestorIds,
  courseIsStaffPreview,
  getCourseTalentIds,
  userCanParticipateInContest,
} from "../../utils/contestEligibility";
import {
  getCurrentLanguageId,
  getDefaultDescription,
  getDefaultName,
  getDefaultValue,
} from "../../utils";
import { KT_COURSE_PARTICIPATION_CHANGED } from "../../utils/contestParticipationEvents";
import { ContestHttpService } from "../../utils/http/contest_http_service";
import { loadCourses, loadTalents, loadUserTalents, resolveMediaUrl } from "../../utils/Network";
import DashboardContestAddTalentModalCta, {
  courseParticipantQuery,
} from "./DashboardContestAddTalentModalCta";

const DEFAULT_CONTEST_IMG = "/assets/images/default.png";
/** Contests created within this many days are treated as "new" for the dashboard widget. */
const NEW_CONTEST_MAX_AGE_DAYS = 30;

function sortByCreatedAtDesc(list) {
  return [...list].sort((a, b) => {
    const tb = new Date(b.created_at || 0).getTime();
    const ta = new Date(a.created_at || 0).getTime();
    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
  });
}

export function pickNewestNewContest(courses) {
  const list = courses || [];
  const preview = list.filter(
    (c) => courseIsStaffPreview(c) && getCourseTalentIds(c).length
  );
  if (preview.length) {
    return sortByCreatedAtDesc(preview)[0] || null;
  }
  const cutoff =
    Date.now() - NEW_CONTEST_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  const candidates = list.filter((c) => {
    if (!c || !c.status) return false;
    if (!getCourseTalentIds(c).length) return false;
    if (!c.created_at) return false;
    const ts = new Date(c.created_at).getTime();
    if (Number.isNaN(ts)) return false;
    return ts >= cutoff;
  });
  return sortByCreatedAtDesc(candidates)[0] || null;
}

function buildTalentNameMap(talentsPayload) {
  const list = Array.isArray(talentsPayload)
    ? talentsPayload
    : talentsPayload?.results || [];
  const map = new Map();
  for (const t of list) {
    if (t && t.id != null) map.set(t.id, t);
  }
  return map;
}

/** Whether the course has a non-empty description for the current language (see `getDefaultDescription`). */
function hasCourseDescription(names) {
  if (!names?.length) return false;
  const language_id = getCurrentLanguageId();
  const entry = names.find((x) => x.language == language_id) || names[0];
  return !!(entry?.description && String(entry.description).trim());
}

export default function WidgetNewContest({ refreshVersion = 0 }) {
  const [contest, setContest] = useState(null);
  const [eligible, setEligible] = useState(false);
  const [talentLabel, setTalentLabel] = useState("");
  /** Same check as `ParticipateButtonSingle`: enrolled in this course for the current user. */
  const [alreadyParticipating, setAlreadyParticipating] = useState(false);
  const [participantQueryDone, setParticipantQueryDone] = useState(false);

  const contestRef = useRef(null);
  const eligibleRef = useRef(false);
  contestRef.current = contest;
  eligibleRef.current = eligible;

  /** Ignore stale `loadCourseParticipant` responses (e.g. initial fetch finishing after participate). */
  const enrollmentReqGen = useRef(0);
  const runCourseEnrollmentFetch = useCallback((courseId) => {
    const requestId = ++enrollmentReqGen.current;
    setParticipantQueryDone(false);
    ContestHttpService.loadCourseParticipant(courseParticipantQuery(courseId))
      .then((cpData) => {
        if (requestId !== enrollmentReqGen.current) return;
        setAlreadyParticipating(!!(cpData?.results?.length));
      })
      .catch(() => {
        if (requestId !== enrollmentReqGen.current) return;
        setAlreadyParticipating(false);
      })
      .finally(() => {
        if (requestId !== enrollmentReqGen.current) return;
        setParticipantQueryDone(true);
      });
  }, []);

  const [{ translations }] = useStateValue();
  const translate = (str) => {
    if (!translations) return str;
    const tr = translations.find((x) => x.key === str);
    if (tr) return getDefaultValue(tr);
    return "" + str;
  };

  useEffect(() => {
    const language_id = getCurrentLanguageId();
    Promise.all([
      loadCourses({ size: 500 }),
      loadUserTalents({ size: 500 }),
      loadTalents(language_id),
    ])
      .then(([coursesList, userTalents, talentsData]) => {
        const newest = pickNewestNewContest(coursesList || []);
        if (!newest) {
          setContest(null);
          setParticipantQueryDone(false);
          return;
        }
        const userSet = collectUserTalentAncestorIds(userTalents || []);
        const canJoin = userCanParticipateInContest(userSet, newest);
        setContest(newest);
        setEligible(canJoin);

        const ids = getCourseTalentIds(newest);
        const byId = buildTalentNameMap(talentsData);
        const names = ids
          .map((id) => {
            const t = byId.get(id);
            return t ? getDefaultName(t.names) : null;
          })
          .filter(Boolean);
        setTalentLabel(
          names.length ? names.join(", ") : ids.join(", ")
        );

        if (!canJoin) {
          setAlreadyParticipating(false);
          setParticipantQueryDone(true);
          return;
        }

        setParticipantQueryDone(false);
        setAlreadyParticipating(false);
        runCourseEnrollmentFetch(newest.id);
      })
      .catch(() => {
        setContest(null);
        setParticipantQueryDone(false);
      });
  }, [runCourseEnrollmentFetch]);

  /** Refresh enrollment when the add-talent modal closes (e.g. after participating). */
  useEffect(() => {
    const el = document.getElementById("addUserTalentModal");
    if (!el || !contest || !eligible) return undefined;
    const onHidden = () => {
      runCourseEnrollmentFetch(contest.id);
    };
    el.addEventListener("hidden.bs.modal", onHidden);
    return () => el.removeEventListener("hidden.bs.modal", onHidden);
  }, [contest, eligible, runCourseEnrollmentFetch]);

  /** Re-check enrollment after participate/unroll from the add-talent modal (no modal close required). */
  useEffect(() => {
    if (!refreshVersion || !contest || !eligible) return undefined;
    runCourseEnrollmentFetch(contest.id);
  }, [refreshVersion, contest, eligible, runCourseEnrollmentFetch]);

  /**
   * Participate / unroll run inside `#addUserTalentModal`, which is not under the same React subtree
   * as this widget, so parent `refreshVersion` bumps can be unreliable. Listen on `window` instead.
   */
  useEffect(() => {
    const handler = (e) => {
      const courseId = e.detail?.courseId;
      const c = contestRef.current;
      if (courseId == null || !c || !eligibleRef.current) return;
      if (Number(courseId) !== Number(c.id)) return;
      runCourseEnrollmentFetch(c.id);
    };
    window.addEventListener(KT_COURSE_PARTICIPATION_CHANGED, handler);
    return () => window.removeEventListener(KT_COURSE_PARTICIPATION_CHANGED, handler);
  }, [runCourseEnrollmentFetch]);

  if (!contest) return null;

  /** If enrolled, hide entirely (My Videos / modal CTAs are enough). */
  if (eligible) {
    if (participantQueryDone && alreadyParticipating) return null;
  }

  const title = getDefaultName(contest.names);

  return (
    <div className="w-100 kfont text-left mb-4 px-0 newest-contest-banner-root dashboard-new-contest-banner-root">
      <div className="newest-contest-banner">
        <div className="newest-contest-banner__media">
          <img
            src={contest.image ? resolveMediaUrl(contest.image) : DEFAULT_CONTEST_IMG}
            alt={title}
            className="newest-contest-banner__img"
          />
        </div>
        <div className="newest-contest-banner__body">
          <h4 className="newest-contest-banner__title font-weight-bold mb-3">
            {title}
            {courseIsStaffPreview(contest) && !contest.status ? (
              <span className="newest-contest-banner__preview-badge">Preview</span>
            ) : null}
          </h4>
          {eligible ? (
            <>
              <p className="newest-contest-banner__hint mb-3 mt-3">
                {alreadyParticipating
                  ? translate(
                      "You are participating in this contest."
                    )
                  : hasCourseDescription(contest.names)
                    ? getDefaultDescription(contest.names)
                    : translate(
                        "You have a matching talent. Open contests to participate."
                      )}
              </p>
              <DashboardContestAddTalentModalCta
                participantQueryDone={participantQueryDone}
                alreadyParticipating={alreadyParticipating}
                translate={translate}
                variant="banner"
              />
            </>
          ) : (
            <>
              <p className="newest-contest-banner__hint mb-3">
                {translate(
                  "This contest is for the following talent categories:"
                )}{" "}
                <strong className="newest-contest-banner__talents">{talentLabel}</strong>
                {". "}
                {translate(
                  "Add one of these talents on your dashboard before you can participate."
                )}
              </p>
              <a
                href="#"
                role="button"
                className="btn newest-contest-banner__btn text-3 font-weight-bold"
                data-toggle="modal"
                data-target="#addUserTalentModal"
                onClick={(e) => e.preventDefault()}
              >
                <i className="fas fa-trophy mr-2" aria-hidden="true" />
                {translate("Participate")}
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
